import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Share2, Plus, Tag, Wrench, ShoppingBag, Phone, IndianRupee, Trash2, Lock, Settings2, Copy, ShoppingCart, Store } from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage } from '@/lib/upload';
import { UploadField } from '@/components/UploadField';

const FALLBACK_CATEGORIES = [
  "Men's Wear", "Women's Wear", "Kids Wear", "Sarees", "Jewellery",
  "Footwear", "Electronics", "Mobile & Accessories", "Home & Kitchen",
  "Beauty & Personal Care", "Grocery", "Sports & Fitness", "Books",
  "Restaurant / Food", "Services", "Other",
];

const Deals = () => {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [category, setCategory] = useState('all');
  const [q, setQ] = useState('');
  const [town, setTown] = useState('');
  const [pincode, setPincode] = useState('');
  const [creating, setCreating] = useState(false);
  const [mySub, setMySub] = useState(null);
  const [membership, setMembership] = useState({ active: false, days_remaining: 0 });
  const [subOpen, setSubOpen] = useState(false);
  const [settings, setSettings] = useState({ entrepreneur_fee: 600, entrepreneur_gst_pct: 18, deal_categories: FALLBACK_CATEGORIES });
  const [subForm, setSubForm] = useState({ utr: '', amount: 708, company_name: '', branch_manager: '', payment_screenshot_url: '' });
  const [subUploading, setSubUploading] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', category: "Men's Wear", custom_category: '', mrp: 0, offer_price: 0, images: [],
    qr_image: '', upi: '', phonepe: '', bank_details: { bank: '', account: '', ifsc: '' },
    contact_phone: '', branch_manager: '', deal_type: 'product',
    brand_name: '', brand_logo_url: '', brand_tagline: '', brand_color: '#1D4ED8',
    whatsapp_number: '', instagram_handle: '', website_url: '', address: '',
  });
  const [createdDeal, setCreatedDeal] = useState(null);
  const [manageDeal, setManageDeal] = useState(null);
  const [manageTab, setManageTab] = useState('links');
  const [orders, setOrders] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (!manageDeal) { setOrders([]); return; }
    (async () => {
      try {
        const o = await api.get(`/deals/${manageDeal.deal_id}/orders`);
        setOrders(o.data.orders || []);
      } catch (e) {
        toast.error('Could not load orders');
      }
    })();
  }, [manageDeal]);

  const updateOrderStatus = async (oid, status) => {
    try {
      await api.patch(`/orders/${oid}/status?status=${status}`);
      setOrders(o => o.map(x => x.order_id === oid ? { ...x, status } : x));
    } catch (e) { toast.error('Failed'); }
  };

  const loadDeals = async () => {
    const params = {};
    if (category !== 'all') params.category = category;
    if (q) params.q = q;
    if (town) params.town = town;
    if (pincode) params.pincode = pincode;
    const { data } = await api.get('/deals', { params });
    setDeals(data);
  };
  const loadMySub = async () => {
    const { data } = await api.get('/entrepreneur/me'); setMySub(data);
    try { const m = await api.get('/membership/status'); setMembership(m.data); } catch {}
  };
  const loadSettings = async () => {
    const { data } = await api.get('/settings/public'); setSettings(data);
    const fee = data.entrepreneur_fee || 600;
    const gst = data.entrepreneur_gst_pct || 18;
    setSubForm(f => ({ ...f, amount: fee + Math.round(fee * gst / 100) }));
  };

  useEffect(() => { loadDeals(); }, [category, q, town, pincode]); // eslint-disable-line
  useEffect(() => { loadMySub(); loadSettings(); }, []);

  const uploadImg = async (e, key) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (key === 'image') {
      // Two-stage compression: client downscale to ≤50 KB, then backend
      // re-encodes with the same budget so the *stored* file is also <50 KB.
      const { compressImage } = await import('@/lib/compress');
      const compressed = await Promise.all(files.map(f => compressImage(f, { targetKB: 50 })));
      const results = await Promise.all(compressed.map(f => uploadImage(f, { targetKb: 50 })));
      const urls = results.filter(Boolean).map(r => r.url);
      if (urls.length) {
        setForm(f => ({ ...f, images: [...f.images, ...urls] }));
        const totalKB = results.filter(Boolean).reduce((s, r) => s + (r.size || 0), 0) / 1024;
        toast.success(`${urls.length} photo${urls.length === 1 ? '' : 's'} added (${totalKB.toFixed(0)} KB stored)`);
      }
    } else {
      const res = await uploadImage(files[0]);
      if (res) setForm(f => ({ ...f, [key]: res.url }));
    }
    e.target.value = '';
  };

  const submit = async () => {
    if (!form.title || !form.description) { toast.error('Title and description required'); return; }
    if (!form.images || form.images.length === 0) { toast.error('Please add at least 1 product photo so customers can see what you sell'); return; }
    let finalCategory = form.category;
    if (finalCategory === 'Other') {
      if (!form.custom_category?.trim()) { toast.error('Please enter your custom category name'); return; }
      finalCategory = form.custom_category.trim();
    }
    try {
      const payload = { ...form, category: finalCategory };
      delete payload.custom_category;
      if (editingId) {
        const { data } = await api.put(`/deals/${editingId}`, payload);
        toast.success('Product updated');
        setCreating(false); setEditingId(null);
        setForm(f => ({ ...f, title: '', description: '', images: [], custom_category: '' }));
        loadDeals();
        return;
      }
      const { data } = await api.post('/deals', payload);
      toast.success('Deal posted — your storefront is live');
      setCreatedDeal(data);
      setCreating(false);
      setForm(f => ({ ...f, title: '', description: '', images: [], custom_category: '' }));
      loadDeals();
    } catch (e) {
      const code = e.response?.data?.detail;
      if (code === 'entrepreneur_subscription_required') {
        toast.error('Take the INDIVALIFE Website Purchase (Yearly or Monthly) to post deals.');
        setCreating(false);
        window.location.href = '/network';
      } else toast.error('Failed');
    }
  };

  const removeImage = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const subscribe = async () => {
    if (!subForm.utr || !subForm.payment_screenshot_url) { toast.error('Upload screenshot + enter UTR'); return; }
    try {
      await api.post('/entrepreneur/subscribe', subForm);
      toast.success('Subscription request submitted — admin will approve within 24h');
      setSubOpen(false); loadMySub();
    } catch (e) { toast.error(`Submission failed: ${e.response?.data?.detail || e.message || 'unknown error'}`); }
  };

  const uploadSubScreenshot = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const res = await uploadImage(file);
    if (res) setSubForm(f => ({ ...f, payment_screenshot_url: res.url }));
    e.target.value = '';
  };

  const removeDeal = async (id) => {
    await api.delete(`/deals/${id}`); loadDeals();
  };

  const startEdit = (d) => {
    setForm({
      title: d.title || '', description: d.description || '',
      category: d.category || "Men's Wear", custom_category: '',
      mrp: d.mrp || 0, offer_price: d.offer_price || 0, images: d.images || [],
      qr_image: d.qr_image || '', upi: d.upi || '', phonepe: d.phonepe || '',
      bank_details: d.bank_details || { bank: '', account: '', ifsc: '' },
      contact_phone: d.contact_phone || '', branch_manager: d.branch_manager || '',
      deal_type: d.deal_type || 'product',
      brand_name: d.brand_name || '', brand_logo_url: d.brand_logo_url || '',
      brand_tagline: d.brand_tagline || '', brand_color: d.brand_color || '#1D4ED8',
      whatsapp_number: d.whatsapp_number || '', instagram_handle: d.instagram_handle || '',
      website_url: d.website_url || '', address: d.address || '',
    });
    setEditingId(d.deal_id);
    setCreating(true);
  };

  const share = (deal, platform) => {
    // Share the OG preview endpoint so WhatsApp/Telegram render the PRODUCT IMAGE,
    // not just a bare link. It redirects instantly to the real storefront.
    const shareUrl = deal.slug
      ? `${process.env.REACT_APP_BACKEND_URL}/api/storefront/share/${deal.slug}`
      : `${window.location.origin}/deals`;
    const text = `${deal.title} — ₹${deal.offer_price} (MRP ₹${deal.mrp})\n${deal.description}\nVia INDIVALIFE`;
    const url = encodeURIComponent(shareUrl);
    const enc = encodeURIComponent(text);
    const map = {
      whatsapp: `https://wa.me/?text=${enc}%20${url}`,
      telegram: `https://t.me/share/url?url=${url}&text=${enc}`,
      instagram: `https://www.instagram.com/`,
    };
    window.open(map[platform], '_blank');
  };

  const isEntrepreneur = membership.active || mySub?.status === 'approved';

  // Personal Stores listing shows ONE catalogue card per store owner. A seller
  // (e.g. "ruhani") who posted many products appears once; the rest of their
  // catalogue is revealed only after entering their personal brand storefront.
  const { storeDeals, ownerCounts } = React.useMemo(() => {
    const seen = new Set();
    const counts = {};
    const out = [];
    for (const d of deals) {
      const key = d.owner_user_id || d.slug || d.owner_name || d.deal_id;
      counts[key] = (counts[key] || 0) + 1;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ ...d, _ownerKey: key });
    }
    return { storeDeals: out, ownerCounts: counts };
  }, [deals]);

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter display">Deals</h1>
          <p className="text-sm text-[var(--bz-muted)] mt-1">Buy &amp; sell locally — products, services, and everything in between.</p>
        </div>
        <div className="flex items-center gap-2">
          {membership.active ? (
            <Badge className="bg-[#1D4ED8]" data-testid="member-unlock-badge">Unlocked via membership · {membership.days_remaining}d left</Badge>
          ) : mySub && mySub.status !== 'approved' ? (
            <Badge variant="outline" className="border-yellow-500 text-yellow-700">Subscription {mySub.status}</Badge>
          ) : isEntrepreneur ? (
            <Badge className="bg-[#1D4ED8]">Entrepreneur ✓</Badge>
          ) : null}
          {!membership.active && (
            <Button variant="outline" onClick={() => window.location.href = '/network'} data-testid="become-entrepreneur-btn" className="gap-2">
              <Lock className="h-4 w-4" /> Become a member (₹{Math.round((settings.membership_fee || 600) * (1 + (settings.membership_gst_pct || 18) / 100))})
            </Button>
          )}
          <Button onClick={() => setCreating(true)} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2" data-testid="post-deal-btn">
            <Plus className="h-4 w-4" /> Post a deal
          </Button>
          {user?.personal_id && (
            <Button variant="outline" onClick={() => window.open(`/store/${user.personal_id}`, '_blank')} className="gap-2 border-[#1D4ED8]/40 text-[#1E3A8A] hover:bg-[#EAF1FF]" data-testid="my-brand-store-btn">
              <Store className="h-4 w-4" /> My brand store
            </Button>
          )}
        </div>
      </div>

      {user?.personal_id && (
        <Card className="p-4 mb-4 border-[#1D4ED8]/30 bg-gradient-to-r from-[#EAF1FF] to-white" data-testid="brand-store-banner">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-10 w-10 rounded-lg bg-[#1D4ED8] text-white grid place-items-center">
              <Store className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">Your Amazon-style brand store is live</div>
              <div className="text-xs text-[var(--bz-muted)] truncate">{`${window.location.origin}/store/${user.personal_id}`}</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/store/${user.personal_id}`); toast.success('Store link copied'); }} data-testid="copy-brand-store-link"><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
              <a href={`https://wa.me/?text=${encodeURIComponent(`Visit my INDIVALIFE store: ${window.location.origin}/store/${user.personal_id}`)}`} target="_blank" rel="noopener noreferrer" className="h-9 px-3 rounded-md inline-flex items-center text-sm text-white" style={{ background: '#25D366' }} data-testid="share-brand-store-wa">Share on WA</a>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 mb-6 grid sm:grid-cols-[1fr_180px_180px_220px] gap-3">
        <Input placeholder="Search by brand, title, town…" value={q} onChange={e => setQ(e.target.value)} data-testid="deals-search" />
        <Input placeholder="Filter by town" value={town} onChange={e => setTown(e.target.value)} data-testid="deals-town" />
        <Input placeholder="Filter by pincode" value={pincode} onChange={e => setPincode(e.target.value)} data-testid="deals-pincode" />
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="deals-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(settings.deal_categories || FALLBACK_CATEGORIES).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      {(town || pincode) && (
        <div className="text-xs text-[var(--bz-muted)] mb-3" data-testid="deals-filter-summary">
          Showing deals near {town && <b>{town}</b>}{town && pincode && ' · '}{pincode && <b>pincode {pincode}</b>}.
          <button className="ml-2 underline" onClick={() => { setTown(''); setPincode(''); }} data-testid="clear-location-filter">Clear location filter</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {storeDeals.length === 0 && <div className="col-span-full p-12 text-center text-sm text-[var(--bz-muted)] bz-card">No stores yet. Be the first to post!</div>}
        {storeDeals.map(d => {
          const moreCount = (ownerCounts[d._ownerKey] || 1) - 1;
          return (
          <Card key={d.deal_id} className="overflow-hidden bz-card group" data-testid={`deal-${d.deal_id}`}>
            {d.slug ? (
              <a href={`/s/${d.slug}`} target="_blank" rel="noopener noreferrer" className="block aspect-square bg-white border-b overflow-hidden cursor-pointer" data-testid={`deal-image-link-${d.deal_id}`} title="Open storefront">
                {d.images?.[0] ? (
                  <img src={d.images[0]} alt={d.title} className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="h-full w-full grid place-items-center text-[var(--bz-muted)] bg-[var(--bz-soft)]"><ShoppingBag className="h-10 w-10" /></div>
                )}
              </a>
            ) : (
              <div className="aspect-square bg-white border-b overflow-hidden">
                {d.images?.[0] ? (
                  <img src={d.images[0]} alt={d.title} className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="h-full w-full grid place-items-center text-[var(--bz-muted)] bg-[var(--bz-soft)]"><ShoppingBag className="h-10 w-10" /></div>
                )}
              </div>
            )}
            <div className="p-4">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-bold tracking-tight text-base line-clamp-1">{d.title}</h3>
                <div className="flex items-center gap-1 shrink-0">
                  {d.in_stock === false && <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-red-400 text-red-700">Out</Badge>}
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{d.category}</Badge>
                </div>
              </div>
              <p className="text-xs text-[var(--bz-muted)] line-clamp-2 mb-3">{d.description}</p>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="text-xl font-black display text-[#1E3A8A]">₹{d.offer_price}</div>
                <div className="text-xs line-through text-[var(--bz-muted)]">₹{d.mrp}</div>
                {d.mrp > d.offer_price && <Badge className="bg-[#1D4ED8] text-[10px]">{Math.round((1 - d.offer_price / d.mrp) * 100)}% off</Badge>}
              </div>
              <div className="text-[11px] text-[var(--bz-muted)] mb-2">📍 {d.owner_town || 'India'} · {d.owner_name}</div>
              {d.slug && (
                <a href={`/s/${d.slug}`} target="_blank" rel="noopener noreferrer" className="mb-3 inline-flex items-center gap-1 text-[11px] font-bold text-[#1D4ED8] hover:underline" data-testid={`enter-store-${d.deal_id}`}>
                  <Store className="h-3 w-3" /> {moreCount > 0 ? `Enter store · +${moreCount} more product${moreCount > 1 ? 's' : ''}` : 'Enter store'}
                </a>
              )}
              <div className="flex items-center gap-1 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => share(d, 'whatsapp')} data-testid={`share-wa-${d.deal_id}`}>WA</Button>
                <Button size="sm" variant="outline" onClick={() => share(d, 'telegram')}>TG</Button>
                <Button size="sm" variant="outline" onClick={() => share(d, 'instagram')}>IG</Button>
                {d.contact_phone && (
                  <Button size="sm" variant="outline" asChild>
                    <a href={`tel:${d.contact_phone}`}><Phone className="h-3 w-3" /></a>
                  </Button>
                )}
                {(d.owner_user_id === user?.user_id || user?.is_admin) && (
                  <>
                    <Button
                      size="sm"
                      variant={d.in_stock === false ? 'outline' : 'default'}
                      onClick={async () => {
                        const next = !(d.in_stock !== false);
                        try {
                          await api.patch(`/deals/${d.deal_id}/stock?in_stock=${next}`);
                          toast.success(next ? 'Marked in stock' : 'Marked out of stock');
                          loadDeals();
                        } catch { toast.error('Failed'); }
                      }}
                      className={d.in_stock === false ? 'border-red-400 text-red-700 h-7 text-xs' : 'bg-blue-600 hover:bg-blue-700 h-7 text-xs'}
                      data-testid={`stock-toggle-${d.deal_id}`}
                    >
                      {d.in_stock === false ? 'Out of stock' : 'In stock'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setManageTab('links'); setManageDeal(d); }} className="text-[#1E3A8A]" data-testid={`customer-link-${d.deal_id}`}>
                      <ShoppingCart className="h-3 w-3 mr-1" />Customer link
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setManageTab('links'); setManageDeal(d); }} className="ml-auto text-[#1E3A8A]" data-testid={`manage-deal-${d.deal_id}`}>
                      <Settings2 className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => startEdit(d)} className="text-[#1E3A8A] h-7 text-xs gap-1" data-testid={`edit-deal-${d.deal_id}`}>
                      <Settings2 className="h-3 w-3" />Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeDeal(d.deal_id)} className="text-red-600" data-testid={`delete-deal-${d.deal_id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card>
          );
        })}
      </div>

      {/* Post / edit deal */}
      <Dialog open={creating} onOpenChange={(o) => { setCreating(o); if (!o) { setEditingId(null); setForm(f => ({ ...f, title: '', description: '', images: [], custom_category: '' })); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bz-scroll">
          <DialogHeader><DialogTitle>{editingId ? 'Edit product' : 'Post a deal'}</DialogTitle></DialogHeader>
          <Tabs defaultValue="basic">
            <TabsList className="grid grid-cols-4">
              <TabsTrigger value="basic">Basics</TabsTrigger>
              <TabsTrigger value="brand">Brand</TabsTrigger>
              <TabsTrigger value="media">Photos</TabsTrigger>
              <TabsTrigger value="payment">Payment</TabsTrigger>
            </TabsList>
            <TabsContent value="basic" className="space-y-3">
              <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="deal-title" /></div>
              <div><Label>Description</Label><Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="deal-description" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, deal_type: v.toLowerCase().includes('service') || v.toLowerCase().includes('restaurant') ? 'service' : 'product' })}>
                    <SelectTrigger data-testid="deal-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{(settings.deal_categories || FALLBACK_CATEGORIES).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>MRP ₹</Label><Input type="number" value={form.mrp} onChange={e => setForm({ ...form, mrp: Number(e.target.value) })} data-testid="deal-mrp" /></div>
                <div><Label>Offer ₹</Label><Input type="number" value={form.offer_price} onChange={e => setForm({ ...form, offer_price: Number(e.target.value) })} data-testid="deal-offer" /></div>
              </div>
              {form.category === 'Other' && (
                <div data-testid="deal-custom-category-wrap">
                  <Label>Custom category name *</Label>
                  <Input value={form.custom_category} onChange={e => setForm({ ...form, custom_category: e.target.value })} placeholder="Type your category, e.g. Handloom Towels" data-testid="deal-custom-category-input" required />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} /></div>
                <div><Label>Branch manager</Label><Input value={form.branch_manager} onChange={e => setForm({ ...form, branch_manager: e.target.value })} /></div>
              </div>
              <div><Label>Address (optional)</Label><Textarea rows={2} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="deal-address" placeholder="Shop No. 12, MG Road…" /></div>
            </TabsContent>
            <TabsContent value="brand" className="space-y-3">
              <div className="rounded-lg bg-[var(--bz-green-tint)] border border-[#1D4ED8]/30 p-3 text-xs flex items-start gap-2">
                <div className="text-base">✨</div>
                <div>
                  This deal automatically inherits your <b>Brand profile</b> — logo, brand name, colour, WhatsApp, Instagram — so you only set them up once. <a href="/profile" className="underline font-bold text-[#1E3A8A]">Edit Brand profile →</a>
                  <div className="mt-1 text-[var(--bz-muted)]">Use the fields below only to override the brand for this one product.</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Brand name <span className="text-[var(--bz-muted)] font-normal">(override)</span></Label><Input value={form.brand_name} onChange={e => setForm({ ...form, brand_name: e.target.value })} data-testid="brand-name-input" placeholder="Leave blank to use saved brand" /></div>
                <div><Label>Brand tagline <span className="text-[var(--bz-muted)] font-normal">(override)</span></Label><Input value={form.brand_tagline} onChange={e => setForm({ ...form, brand_tagline: e.target.value })} placeholder="Leave blank to use saved brand" data-testid="brand-tagline-input" /></div>
              </div>
            </TabsContent>
            <TabsContent value="media" className="space-y-3">
              <Label className="text-base font-bold">Product photos · upload as many as you want</Label>
              <p className="text-xs text-[var(--bz-muted)]">Tap below to pick photos from your phone. First image is the cover. <b>At least 1 photo is required</b> so customers can see your product.</p>
              <label className="block cursor-pointer mt-2" data-testid="deal-image-dropzone">
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadImg(e, 'image')} data-testid="deal-image-input" />
                <div className="border-2 border-dashed border-[#1D4ED8]/40 bg-[#EAF1FF]/40 hover:bg-[#EAF1FF] hover:border-[#1D4ED8] rounded-xl px-4 py-8 text-center transition">
                  <div className="text-3xl">📸</div>
                  <div className="mt-2 font-bold text-sm text-[#1E3A8A]">Tap to add product photos</div>
                  <div className="text-[11px] text-[var(--bz-muted)] mt-1">You can select multiple files at once · JPG / PNG / HEIC</div>
                </div>
              </label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {form.images.map((u, i) => (
                  <div key={u} className="relative group" data-testid={`deal-image-${i}`}>
                    <img src={u} alt="" className="aspect-square object-cover rounded-md border" />
                    {i === 0 && <Badge className="absolute bottom-1 left-1 text-[9px] bg-[#1D4ED8]">COVER</Badge>}
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full h-6 w-6 text-sm grid place-items-center shadow-md" data-testid={`deal-image-remove-${i}`}>×</button>
                  </div>
                ))}
              </div>
              {form.images.length === 0 && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">⚠️ No photos yet — your storefront will show a placeholder icon until you add at least 1 photo.</div>}
              <p className="text-[11px] text-[var(--bz-muted)]">Pro tip: 3-5 photos from different angles make products feel premium and trustworthy.</p>
            </TabsContent>
            <TabsContent value="payment" className="space-y-3">
              <Label>QR code image</Label>
              <input type="file" accept="image/*" onChange={(e) => uploadImg(e, 'qr_image')} />
              {form.qr_image && <img src={form.qr_image} alt="QR" className="h-32" />}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>UPI ID</Label><Input value={form.upi} onChange={e => setForm({ ...form, upi: e.target.value })} /></div>
                <div><Label>PhonePe</Label><Input value={form.phonepe} onChange={e => setForm({ ...form, phonepe: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Bank</Label><Input value={form.bank_details.bank} onChange={e => setForm({ ...form, bank_details: { ...form.bank_details, bank: e.target.value } })} /></div>
                <div><Label>Account</Label><Input value={form.bank_details.account} onChange={e => setForm({ ...form, bank_details: { ...form.bank_details, account: e.target.value } })} /></div>
                <div><Label>IFSC</Label><Input value={form.bank_details.ifsc} onChange={e => setForm({ ...form, bank_details: { ...form.bank_details, ifsc: e.target.value } })} /></div>
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="submit-deal-button">{editingId ? 'Save changes' : 'Publish deal'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Storefront ready — share dialog */}
      <Dialog open={!!createdDeal} onOpenChange={(o) => { if (!o) setCreatedDeal(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">🎉 Your storefront is live!</DialogTitle>
          </DialogHeader>
          {createdDeal && (() => {
            const storefrontUrl = `${window.location.origin}/s/${createdDeal.slug}`;
            const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/storefront/share/${createdDeal.slug}`;
            const shareText = `Check out ${createdDeal.brand_name || createdDeal.title} on INDIVALIFE — ${createdDeal.title} at ₹${createdDeal.offer_price}\n${previewUrl}`;
            const waLink = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
            return (
              <div className="space-y-3">
                <div className="rounded-lg border-2 border-[#1D4ED8] bg-[var(--bz-green-tint)] p-3 break-all font-mono text-sm" data-testid="storefront-url">
                  {storefrontUrl}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <a href={waLink} target="_blank" rel="noopener noreferrer" className="h-10 rounded-md text-white font-bold text-sm flex items-center justify-center gap-1.5" style={{ background: '#25D366' }} data-testid="share-store-whatsapp">📱 WhatsApp</a>
                  <button onClick={() => { navigator.clipboard.writeText(storefrontUrl); toast.success('Copied'); }} className="h-10 rounded-md bg-[var(--bz-soft)] font-bold text-sm" data-testid="share-store-copy">Copy link</button>
                  <a href={storefrontUrl} target="_blank" rel="noopener noreferrer" className="h-10 rounded-md bg-[#1D4ED8] text-white font-bold text-sm flex items-center justify-center" data-testid="share-store-open">Open</a>
                </div>
                <p className="text-[11px] text-[var(--bz-muted)]">Paste this link anywhere — WhatsApp, Instagram bio, Facebook, SMS — and customers land on your branded INDIVALIFE store.</p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Manage Storefront — partners, orders, share */}
      <Dialog open={!!manageDeal} onOpenChange={(o) => { if (!o) setManageDeal(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage storefront · {manageDeal?.brand_name || manageDeal?.title}</DialogTitle>
          </DialogHeader>
          {manageDeal && (() => {
            const storefrontUrl = `${window.location.origin}/s/${manageDeal.slug}`;
            const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/storefront/share/${manageDeal.slug}`;
            const shareImgUrl = `${process.env.REACT_APP_BACKEND_URL}/api/deals/${manageDeal.deal_id}/share-image`;
            return (
              <Tabs value={manageTab} onValueChange={setManageTab}>
                <TabsList className="grid grid-cols-2">
                  <TabsTrigger value="links">Share links</TabsTrigger>
                  <TabsTrigger value="orders">Orders <span className="ml-1 text-[10px]">{orders.length}</span></TabsTrigger>
                </TabsList>

                <TabsContent value="links" className="space-y-4 mt-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold mb-1">Customer link · for direct sales</div>
                    <div className="flex gap-2">
                      <Input readOnly value={storefrontUrl} data-testid="customer-link-input" />
                      <Button variant="outline" onClick={() => { navigator.clipboard.writeText(storefrontUrl); toast.success('Copied'); }} data-testid="copy-customer-link"><Copy className="h-4 w-4" /></Button>
                      <a href={`https://wa.me/?text=${encodeURIComponent(`Buy ${manageDeal.title} at ₹${manageDeal.offer_price}\n${previewUrl}`)}`} target="_blank" rel="noopener noreferrer" className="h-9 px-3 rounded-md inline-flex items-center text-sm text-white" style={{ background: '#25D366' }}>WA</a>
                    </div>
                    <p className="text-[11px] text-[var(--bz-muted)] mt-1">Customers visiting this URL see your real price and order directly from you. The WhatsApp button shares a link that shows your product photo preview.</p>
                  </div>
                  <div className="border-t pt-4">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold mb-2">WhatsApp-ready share image</div>
                    <img src={shareImgUrl} alt="" className="w-48 h-48 rounded-md border" data-testid="share-image-preview" />
                    <div className="mt-2 flex gap-2">
                      <a href={shareImgUrl} download={`${manageDeal.slug}.png`} className="text-xs text-[#1E3A8A] underline">Download PNG</a>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="orders" className="space-y-2 mt-3">
                  {orders.length === 0 && <div className="text-xs text-[var(--bz-muted)] text-center py-6">No orders yet.</div>}
                  {orders.map(o => (
                    <div key={o.order_id} className="bz-card p-3" data-testid={`order-row-${o.order_id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate">{o.customer_name} · {o.customer_phone}</div>
                          <div className="text-[11px] text-[var(--bz-muted)]">{new Date(o.created_at).toLocaleString()} · {o.quantity} × ₹{o.unit_price_final} = <b>₹{o.total_amount}</b></div>
                          {o.partner_name && <div className="text-[10px] text-[#1E3A8A]">via partner {o.partner_name} · commission ₹{o.partner_commission}</div>}
                          {o.customer_address && <div className="text-[11px] text-[var(--bz-muted)] mt-1">📍 {o.customer_address}</div>}
                        </div>
                        <Select value={o.status} onValueChange={(v) => updateOrderStatus(o.order_id, v)}>
                          <SelectTrigger className="w-32 text-xs" data-testid={`order-status-${o.order_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['pending', 'accepted', 'shipped', 'delivered', 'cancelled'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Entrepreneur subscription */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Become an entrepreneur</DialogTitle></DialogHeader>
          <p className="text-sm text-[var(--bz-muted)]">Pay ₹{settings.entrepreneur_fee} + {settings.entrepreneur_gst_pct}% GST = <b>₹{subForm.amount}</b> via UPI to <span className="font-mono">{settings.company_upi}</span> and upload the screenshot below. Admin approval within 24h.</p>
          <div className="space-y-3">
            <div><Label>Company name (optional)</Label><Input value={subForm.company_name} onChange={e => setSubForm({ ...subForm, company_name: e.target.value })} /></div>
            <div><Label>Branch manager</Label><Input value={subForm.branch_manager} onChange={e => setSubForm({ ...subForm, branch_manager: e.target.value })} /></div>
            <div><Label>UTR / Transaction ID</Label><Input value={subForm.utr} onChange={e => setSubForm({ ...subForm, utr: e.target.value })} data-testid="ent-utr" /></div>
            <div><Label>Amount paid</Label><Input type="number" value={subForm.amount} onChange={e => setSubForm({ ...subForm, amount: Number(e.target.value) })} /></div>
            <div><Label>Payment screenshot</Label>
              <UploadField
                value={subForm.payment_screenshot_url}
                onChange={(url) => setSubForm(f => ({ ...f, payment_screenshot_url: url }))}
                onStatusChange={setSubUploading}
                testId="ent-screenshot"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubOpen(false)}>Cancel</Button>
            <Button onClick={subscribe} disabled={subUploading || !subForm.payment_screenshot_url} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="submit-entrepreneur-button">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brand-store partners dialog removed per product decision */}

    </div>
  );
};

export default Deals;
